import { mapGetters } from 'vuex';

/**
 * Permission rules for a single conversation, shared by the two components that render one:
 * the inbox modal (`conversations/conversation-thread.vue`) and the full page thread
 * (`MessagingThread.vue`). Both carried their own copies, which had drifted apart — the same
 * button was gated on a different rule depending on how you opened the conversation.
 *
 * The shape mirrors `ConversationPolicy`: the base verb is the licence to act on conversations
 * at all, and either being an admin of *this* conversation or holding the All variant lifts the
 * scope from "the ones I run" to "any of them". Delete follows the same shape after ENT-847.
 *
 * RBAC roles are only ever assigned to employees. A client or external participant holds no
 * verbs at all, so every `can()` below is false for them — hence the `isEmployee` guards. A
 * client can still be the admin of a conversation, and clients do reach the inbox modal through
 * `layouts/client.vue`, so without those guards a client would lose control of a conversation
 * they run. Their own create/update journeys live in the client-facing app, not here.
 *
 * The computed gates expect the component to hold the conversation in `conversation`. The methods
 * take one as an argument instead, so an inbox row can ask about the conversation it renders.
 */
export default {
  computed: {
    ...mapGetters('user', ['isEmployee', 'currentAccountUuid']),

    isConversationAdmin() {
      return Boolean(this.conversation?.is_admin);
    },

    /*
      Renaming and archiving. Archiving shares this gate rather than being treated as a personal
      preference because for an employee the API archives the conversation for every participant,
      not just for them.
    */
    canUpdateConversation() {
      if (!this.isEmployee) {
        return this.isConversationAdmin;
      }

      return this.can('conversations:update') && (this.isConversationAdmin || this.can('conversations:updateAll'));
    },

    /*
      Mirrors ConversationPolicy::delete as reworked by ENT-847 — conversations:delete is required
      either way, then conversations:deleteAll covers any conversation while an admin of this one
      (its creator, or anyone since promoted) can always delete it.

      Deleting stays employee-only, as it was before: it removes the conversation for everybody,
      and clients have never been offered it here.
    */
    canDeleteConversation() {
      if (!this.isEmployee) {
        return false;
      }

      return this.can('conversations:delete') && (this.isConversationAdmin || this.can('conversations:deleteAll'));
    },

    /*
      Mirrors ConversationPolicy::manageParticipants, promote and revoke. Deliberately not gated
      on isEmployee — a client admin manages the participants of their own conversation, which is
      the one conversation control they already had here.
    */
    canUpdateParticipants() {
      return this.isConversationAdmin || this.can('conversations:membership');
    },
  },

  methods: {
    isParticipantOf(conversation) {
      return Boolean(conversation?.participants?.some((participant) => participant.uuid === this.currentAccountUuid));
    },

    /*
      Whether a conversation listed in an inbox can actually be opened, so we never offer a row
      that dead ends. Mirrors ConversationPolicy::view as a user reads it: readAll reaches any
      conversation, otherwise you reach the ones you take part in.

      The API scopes its conversation listings on groups:readAll rather than conversations:readAll,
      so it can hand us rows that then 403 on open. Clients and external participants are exempt:
      they hold no RBAC verbs at all, and every listing they get is already scoped to conversations
      they participate in.
    */
    canViewConversation(conversation) {
      if (!this.isEmployee) {
        return true;
      }

      return this.can('conversations:readAll') || this.isParticipantOf(conversation);
    },
  },
};
